import { aggregateTimeLogs } from './aggregateTimeLogs';

/**
 * Build a flat report dataset from selected modules' raw data.
 * - Single module: pick the requested fields and return.
 * - Multiple modules: merge by (project_id | user_id | task_id) with cartesian
 *   fan-out for one-to-many relationships (Project → many Time Logs).
 * Aggregation for time_logs is applied at the end via aggregateTimeLogs().
 */
export function buildReportData({ selectedModules, selectedFields, modulesData }) {
  // Single-module path
  if (selectedModules.length === 1) {
    const mod = selectedModules[0];
    const data = (modulesData[mod] || []).map((row) => {
      const mapped = { _source_module: mod };
      selectedFields.forEach((f) => {
        const [fMod, fField] = f.split('.');
        if (fMod === mod) mapped[f] = row[fField];
      });
      return mapped;
    });
    return aggregateTimeLogs(data, selectedModules, selectedFields);
  }

  // Cross-module path
  const primaryModule = selectedModules[0];
  const primaryData = modulesData[primaryModule] || [];

  // Build array-of-matches lookups so one primary row can fan out to many merged rows
  const secLookups = {};
  for (let i = 1; i < selectedModules.length; i++) {
    const secMod = selectedModules[i];
    const secData = modulesData[secMod] || [];
    const byProject = {};
    const byUser = {};
    const byTask = {};
    secData.forEach((s) => {
      if (s.project_id) (byProject[s.project_id] = byProject[s.project_id] || []).push(s);
      if (s.user_id) (byUser[s.user_id] = byUser[s.user_id] || []).push(s);
      if (s.task_id) (byTask[s.task_id] = byTask[s.task_id] || []).push(s);
    });
    secLookups[secMod] = { byProject, byUser, byTask };
  }

  const merged = [];
  primaryData.forEach((primaryRow) => {
    const baseRow = { _source_module: primaryModule };
    selectedFields.forEach((f) => {
      const [fMod, fField] = f.split('.');
      if (fMod === primaryModule) baseRow[f] = primaryRow[fField];
    });

    const matchesPerSec = [];
    for (let i = 1; i < selectedModules.length; i++) {
      const secMod = selectedModules[i];
      const lookup = secLookups[secMod];
      const matches =
        (primaryRow.project_id && lookup.byProject[primaryRow.project_id]) ||
        (primaryRow.user_id && lookup.byUser[primaryRow.user_id]) ||
        (primaryRow.task_id && lookup.byTask[primaryRow.task_id]) ||
        [null];
      matchesPerSec.push({ secMod, matches });
    }

    const expand = (idx, current) => {
      if (idx >= matchesPerSec.length) {
        merged.push(current);
        return;
      }
      const { secMod, matches } = matchesPerSec[idx];
      matches.forEach((match) => {
        const next = { ...current };
        selectedFields.forEach((f) => {
          const [fMod, fField] = f.split('.');
          if (fMod === secMod) next[f] = match ? match[fField] : '-';
        });
        expand(idx + 1, next);
      });
    };
    expand(0, baseRow);
  });

  return aggregateTimeLogs(merged, selectedModules, selectedFields);
}
